// Ren geometri-hjelper for mini-kartet i farevarsel-modalen.
// Projiserer et GeoJSON-polygon (Polygon/MultiPolygon) til SVG-koordinater
// slik at det kan tegnes som en statisk illustrasjon av det berørte området.
//
// Projeksjonen er en enkel equirektangulær projeksjon med cos(lat)-korreksjon
// på øst-vest-aksen, slik at formen ikke strekkes ut på nordlige breddegrader
// (uten korreksjon blir Norge f.eks. altfor bred). Dette er ikke kartografisk
// nøyaktig, men mer enn godt nok for en liten thumbnail.

export interface SvgDims {
  width: number;
  height: number;
  padding: number;
}

export const DEFAULT_SVG_DIMS: SvgDims = { width: 220, height: 150, padding: 12 };

export interface WarningAreaGeometry {
  /** SVG path-data for polygonet (kan inneholde flere delflater/hull, fill-rule evenodd). */
  pathData: string;
  /** Prosjektert posisjon for «du er her»-prikken, hvis et senter ble oppgitt og faller innenfor. */
  dot: { cx: number; cy: number } | null;
  viewBox: string;
}

type Ring = number[][]; // [ [lon,lat], ... ]

/**
 * Trekk ut alle ytre/indre ringer fra en Polygon- eller MultiPolygon-geometri.
 * Returnerer tom liste for alt annet.
 */
export function extractRings(geometry: unknown): Ring[] {
  const g = geometry as { type?: string; coordinates?: unknown } | null | undefined;
  if (!g || !Array.isArray(g.coordinates)) return [];

  if (g.type === 'Polygon') {
    // coordinates: Ring[]
    return (g.coordinates as unknown[]).filter(isRing);
  }
  if (g.type === 'MultiPolygon') {
    // coordinates: Polygon[]  (hver Polygon er Ring[])
    const rings: Ring[] = [];
    for (const poly of g.coordinates as unknown[]) {
      if (Array.isArray(poly)) {
        for (const ring of poly) {
          if (isRing(ring)) rings.push(ring as Ring);
        }
      }
    }
    return rings;
  }
  return [];
}

function isRing(r: unknown): r is Ring {
  return (
    Array.isArray(r) &&
    r.length >= 3 &&
    r.every(
      pt =>
        Array.isArray(pt) &&
        pt.length >= 2 &&
        typeof pt[0] === 'number' &&
        typeof pt[1] === 'number' &&
        Number.isFinite(pt[0]) &&
        Number.isFinite(pt[1]),
    )
  );
}

/**
 * Bygg SVG-geometri for et farevarsel-polygon.
 *
 * @param geometry  GeoJSON-geometri (Polygon eller MultiPolygon), typisk fra `feature.geometry`.
 * @param center    Valgfritt kartsenter { lat, lon } — tegnes som «du er her»-prikk.
 * @param dims      SVG-dimensjoner (viewBox + padding).
 * @returns         Geometri klar for rendering, eller null hvis polygonet ikke kan tegnes.
 */
export function buildWarningAreaGeometry(
  geometry: unknown,
  center?: { lat: number; lon: number } | null,
  dims: SvgDims = DEFAULT_SVG_DIMS,
): WarningAreaGeometry | null {
  const rings = extractRings(geometry);
  if (rings.length === 0) return null;

  // Bounding box i lon/lat.
  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;
  for (const ring of rings) {
    for (const [lon, lat] of ring) {
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
  }
  if (!Number.isFinite(minLon) || !Number.isFinite(minLat)) return null;

  // cos(lat)-korreksjon på øst-vest-aksen.
  const midLatRad = (((minLat + maxLat) / 2) * Math.PI) / 180;
  const kx = Math.max(Math.cos(midLatRad), 1e-6);

  const minX = minLon * kx;
  const maxX = maxLon * kx;
  const spanX = maxX - minX;
  const spanY = maxLat - minLat;
  if (spanX <= 0 && spanY <= 0) return null; // degenerert til ett punkt

  const { width: W, height: H, padding: pad } = dims;
  const availW = W - 2 * pad;
  const availH = H - 2 * pad;

  // Skala som bevarer aspekt; håndter degenererte akser.
  let scale: number;
  if (spanX <= 0) scale = availH / spanY;
  else if (spanY <= 0) scale = availW / spanX;
  else scale = Math.min(availW / spanX, availH / spanY);
  if (!Number.isFinite(scale) || scale <= 0) return null;

  const drawnW = spanX * scale;
  const drawnH = spanY * scale;
  const offsetX = pad + (availW - drawnW) / 2;
  const offsetY = pad + (availH - drawnH) / 2;

  const round = (n: number) => Math.round(n * 100) / 100;
  const toSvg = (lon: number, lat: number): [number, number] => [
    round(offsetX + (lon * kx - minX) * scale),
    round(offsetY + (maxLat - lat) * scale), // inverter Y: lat opp → SVG y ned
  ];

  const subPaths: string[] = [];
  for (const ring of rings) {
    const pts = ring.map(([lon, lat]) => toSvg(lon, lat));
    if (pts.length < 3) continue;
    const [first, ...rest] = pts;
    subPaths.push(
      `M ${first[0]} ${first[1]} ` + rest.map(([x, y]) => `L ${x} ${y}`).join(' ') + ' Z',
    );
  }
  if (subPaths.length === 0) return null;

  let dot: { cx: number; cy: number } | null = null;
  if (center && Number.isFinite(center.lat) && Number.isFinite(center.lon)) {
    const [cx, cy] = toSvg(center.lon, center.lat);
    // Bare vis prikken hvis den faller innenfor viewBox-en.
    if (cx >= 0 && cx <= W && cy >= 0 && cy <= H) dot = { cx, cy };
  }

  return {
    pathData: subPaths.join(' '),
    dot,
    viewBox: `0 0 ${W} ${H}`,
  };
}
